import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CLIENT_NOTIFICATION_EVENTS,
  CLIENT_NOTIFICATION_GROUPS,
  DEFAULT_CLIENT_NOTIFICATIONS,
  type ClientNotificationEvent,
  type ClientNotificationGroup,
  type ClientNotifications,
} from "@goyone/shared";
import { api } from "../../lib/api";
import { useTenantProfile } from "../../lib/settings";
import { Icon } from "../../components/Icon";
import { ErrorState, PageHead } from "../../components/ui";

/**
 * الأتمتة — ما يفعله النظام من تلقاء نفسه.
 *
 * أوّلُ ما فيه إشعاراتُ العميل بالبريد: تسعُ لحظاتٍ في دورة العمل يخرج
 * عندها بريدٌ إليه، مقسومةً أربعَ مجموعات — الحجوزات والأنشطة والمال
 * والمشروعات — تُفتح وتُغلق مجموعةً أو حدثًا حدثًا، ولكلّ حدثٍ سطرٌ
 * يُلحق برسالته. وقواعدُ الأتمتة الأعمّ («إن وقع كذا فافعل كذا») لم
 * تُبنَ بعد، ويُقال ذلك هنا لا يُخفى.
 */

/** لونُ كلّ مجموعة — علامةٌ على الحافة والأيقونة من اللوحة المُتحقَّق منها. */
const GROUP_TONE: Readonly<Record<ClientNotificationGroup, string>> = {
  bookings: "var(--mv-3)",
  activities: "var(--mv-2)",
  finance: "var(--mv-4)",
  projects: "var(--mv-1)",
};
const GROUP_ICON: Readonly<
  Record<ClientNotificationGroup, "calendar" | "clock" | "wallet" | "briefcase">
> = {
  bookings: "calendar",
  activities: "clock",
  finance: "wallet",
  projects: "briefcase",
};

interface MailAccountRow {
  id: string;
  isActive: boolean;
}
interface MailAccountsPage {
  items: MailAccountRow[];
}

function Switch({
  on,
  onChange,
  label,
  small,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  small?: boolean;
}) {
  return (
    <label className={`cn-switch${small ? " is-small" : ""}`}>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="cn-switch-track" aria-hidden="true">
        <span className="cn-switch-knob" />
      </span>
    </label>
  );
}

export function Automation() {
  const queryClient = useQueryClient();
  const profile = useTenantProfile();
  const [cn, setCn] = useState<ClientNotifications>(
    DEFAULT_CLIENT_NOTIFICATIONS,
  );
  const [dirty, setDirty] = useState(false);
  const [openNote, setOpenNote] = useState<ClientNotificationEvent | null>(
    null,
  );

  /* صندوق البريد شرطُ الخروج كلِّه — بلا صندوقٍ نشط لا يخرج شيء مهما فُتح. */
  const accounts = useQuery({
    queryKey: ["mail", "accounts"],
    queryFn: () => api<MailAccountsPage>("/mail/accounts"),
  });
  const hasMailbox = (accounts.data?.items ?? []).some((a) => a.isActive);

  /* الحالة تُملأ من الخادم مرّةً — لا في كل تصييرٍ فيُمسح ما يُكتب. */
  useEffect(() => {
    const s = profile.data?.settings;
    if (!s || dirty) return;
    setCn(s.clientNotifications ?? DEFAULT_CLIENT_NOTIFICATIONS);
  }, [profile.data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api("/settings/preferences", {
        method: "PATCH",
        body: { clientNotifications: cn },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
      setDirty(false);
    },
  });

  const patch = (fn: (c: ClientNotifications) => ClientNotifications) => {
    setCn((c) => fn(c));
    setDirty(true);
  };
  const setGroup = (g: ClientNotificationGroup, v: boolean) =>
    patch((c) => ({ ...c, groups: { ...c.groups, [g]: v } }));
  const setEvent = (
    e: ClientNotificationEvent,
    v: Partial<{ enabled: boolean; note: string }>,
  ) =>
    patch((c) => ({
      ...c,
      events: { ...c.events, [e]: { ...c.events[e], ...v } },
    }));

  if (profile.isError) {
    return (
      <ErrorState error={profile.error} onRetry={() => profile.refetch()} />
    );
  }

  const activeCount = CLIENT_NOTIFICATION_EVENTS.filter(
    (e) => cn.enabled && cn.groups[e.group] && cn.events[e.key].enabled,
  ).length;

  return (
    <div className="page">
      <PageHead
        title="الأتمتة"
        subtitle="ما يفعله النظام من تلقاء نفسه — وأوّله ما يخبر به العميل"
        actions={
          <button
            className="btn btn-primary"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            <Icon name="check" size={14} />
            {save.isPending ? "جارٍ الحفظ" : "حفظ"}
          </button>
        }
      />

      {save.isSuccess && !dirty && (
        <div className="alert alert-info mb-4">
          <Icon name="check" size={15} />
          <div>حُفظت الإعدادات. تسري على ما يقع بعد الآن.</div>
        </div>
      )}
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : "تعذّر الحفظ."}
        </div>
      )}

      {/*
        الصندوق أولًا.
        بلا صندوقٍ مربوطٍ نشط لا تخرج رسالةٌ مهما فُتحت المفاتيح — فيُقال
        ذلك فوق المفاتيح لا يُكتشف من عميلٍ لم يصله شيء.
      */}
      {accounts.data && !hasMailbox && (
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={15} />
          <div>
            لا صندوق بريدٍ نشط — لن يخرج بريدٌ إلى أيّ عميل حتى يُربط واحد.{" "}
            <Link to="/settings/mail" className="link">
              اربط صندوقًا من إعدادات البريد
            </Link>
            .
          </div>
        </div>
      )}

      {/* ---------------- إشعارات العميل ---------------- */}
      <section className="cn">
        <header className="cn-head">
          <div className="cn-head-text">
            <h2 className="card-title">
              <Icon name="mail" size={16} /> إشعارات العميل بالبريد
            </h2>
            <p className="tiny mute">
              رسائل تخرج إلى بريد العميل ساعةَ وقوع الحدث — تحمل أرقامها وقيمها
              في متنها، بلا رابطٍ يطلب حسابًا لا يملكه.
            </p>
          </div>
          <div className="cn-master">
            <span className="cn-master-text">
              <strong>
                {cn.enabled ? "الإشعارات تعمل" : "الإشعارات موقوفة"}
              </strong>
              <span className="tiny mute">
                {cn.enabled
                  ? `${activeCount} من ${CLIENT_NOTIFICATION_EVENTS.length} أحداثٍ تخرج`
                  : "لا يخرج بريدٌ من أيّ حدث"}
              </span>
            </span>
            <Switch
              on={cn.enabled}
              onChange={(v) => patch((c) => ({ ...c, enabled: v }))}
              label="المفتاح العامّ"
            />
          </div>
        </header>

        <div className={`cn-groups${cn.enabled ? "" : " is-off"}`}>
          {CLIENT_NOTIFICATION_GROUPS.map((g) => {
            const on = cn.groups[g.key];
            const events = CLIENT_NOTIFICATION_EVENTS.filter(
              (e) => e.group === g.key,
            );
            const live = events.filter(
              (e) => e.key in cn.events && cn.events[e.key].enabled,
            ).length;
            return (
              <article
                key={g.key}
                className={`cn-group${on ? "" : " is-off"}`}
                style={{ "--cn-tone": GROUP_TONE[g.key] } as CSSProperties}
              >
                <header className="cn-group-head">
                  <span className="cn-group-icon">
                    <Icon name={GROUP_ICON[g.key]} size={16} />
                  </span>
                  <span className="cn-group-text">
                    <strong>{g.label}</strong>
                    <span className="tiny mute">{g.hint}</span>
                  </span>
                  <span className="cn-group-count num" title="الأحداث الفعّالة">
                    {on ? live : 0}/{events.length}
                  </span>
                  <Switch
                    on={on}
                    onChange={(v) => setGroup(g.key, v)}
                    label={`مجموعة ${g.label}`}
                  />
                </header>

                <ul className="cn-events">
                  {events.map((e) => {
                    const ev = cn.events[e.key];
                    const noteOpen = openNote === e.key;
                    return (
                      <li
                        key={e.key}
                        className={`cn-event${ev.enabled && on ? "" : " is-off"}`}
                      >
                        <div className="cn-event-row">
                          <span className="cn-event-dot" aria-hidden="true" />
                          <span className="cn-event-text">
                            <strong>{e.label}</strong>
                            <span className="tiny mute">{e.hint}</span>
                          </span>
                          <button
                            type="button"
                            className={`btn btn-sm btn-ghost cn-note-btn${ev.note ? " has-note" : ""}`}
                            onClick={() => setOpenNote(noteOpen ? null : e.key)}
                            title="سطرٌ يُلحق بالرسالة"
                            aria-expanded={noteOpen}
                          >
                            <Icon name="note" size={13} />
                            {ev.note ? "سطرٌ مُلحق" : "أضِف سطرًا"}
                          </button>
                          <Switch
                            small
                            on={ev.enabled}
                            onChange={(v) => setEvent(e.key, { enabled: v })}
                            label={e.label}
                          />
                        </div>
                        {noteOpen && (
                          <label className="cn-note">
                            <span className="tiny mute">
                              يُلحق بآخر رسالة «{e.label}» — شرطٌ خاصّ أو من
                              يُتواصَل معه.
                            </span>
                            <textarea
                              className="textarea"
                              rows={2}
                              value={ev.note}
                              onChange={(ev2) =>
                                setEvent(e.key, { note: ev2.target.value })
                              }
                              placeholder="للاستفسار تواصلوا مع حسابات العملاء على 0100 000 0000."
                            />
                          </label>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </article>
            );
          })}
        </div>

        {/* الذيل: توقيعٌ واحد يُختم به كلُّ بريدٍ للعميل. */}
        <label className="field cn-footer">
          <span className="label">ذيل الرسائل</span>
          <textarea
            className="textarea"
            rows={3}
            value={cn.footer}
            onChange={(e) => patch((c) => ({ ...c, footer: e.target.value }))}
            placeholder={
              "مع تحيات فريق الشركة\nهاتف: 0100 000 0000 — www.example.com"
            }
          />
          <span className="tiny mute">
            يُختم به كلُّ بريدٍ يخرج إلى عميلٍ من الأحداث أعلاه.
          </span>
        </label>
      </section>

      {/* ---------------- ما لم يُبنَ بعد ---------------- */}
      <section className="cn cn-soon">
        <header className="cn-head">
          <div className="cn-head-text">
            <h2 className="card-title">
              <Icon name="zap" size={16} /> قواعد الأتمتة
            </h2>
            <p className="tiny mute">
              «إن وقع كذا فافعل كذا»: تحويلُ عميلٍ محتملٍ لم يُتابَع، وتذكيرُ
              فاتورةٍ تأخّرت، وتكليفٌ عند فوز صفقة. لم تُبنَ بعد — وموضعُها هنا
              محجوز.
            </p>
          </div>
          <span className="badge badge-neutral">قريبًا</span>
        </header>
      </section>
    </div>
  );
}
